import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import ImageUpload from './ImageUpload';
import Swal from 'sweetalert2';
import { Save, Layers, List, Image as ImageIcon, Sparkles, Plus, Trash2, Globe } from 'lucide-react';

const divisionsList = [
  { id: 'administration', name: 'Administration' },
  { id: 'accounts', name: 'Account' },
  { id: 'labour-time', name: 'Labour Time Office' },
  { id: 'engineering', name: 'Engineering Department' },
  { id: 'cane-development', name: 'Cane Development / Farming' },
  { id: 'purchase', name: 'Purchase Department' },
  { id: 'godown', name: 'Godown Department' },
  { id: 'security', name: 'Security Department' }
];

const emptyDivisionStructure = {
  heroTitle: '',
  heroTitleMr: '',
  heroDesc: '',
  heroDescMr: '',
  introLabel: '',
  introLabelMr: '',
  introTitle: '',
  introTitleMr: '',
  introDesc: '',
  introDescMr: '',
  introImage: '',
  introSubCards: [{}, {}],
  responsibilitiesTitle: '',
  responsibilitiesTitleMr: '',
  responsibilities: [],
  servicesTitle: '',
  servicesTitleMr: '',
  services: [],
  galleryImages: []
};

const AdminDivisions = () => {
  const [selectedDivision, setSelectedDivision] = useState('administration');
  const [activeFormTab, setActiveFormTab] = useState('hero-overview'); // 'hero-overview' | 'lists' | 'gallery'
  const [formLang, setFormLang] = useState('en'); // 'en' | 'mr'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);

  // Fetch division details directly from Firestore
  useEffect(() => {
    const fetchDivisionData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'divisions', selectedDivision);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const dbData = docSnap.data();
          setFormData({
            ...emptyDivisionStructure,
            ...dbData,
            introSubCards: dbData.introSubCards || [{}, {}],
            services: dbData.services || [],
            galleryImages: dbData.galleryImages || []
          });
        } else {
          setFormData(JSON.parse(JSON.stringify(emptyDivisionStructure)));
        }
      } catch (error) {
        console.error("Error loading division data:", error);
        Swal.fire('Error', 'Failed to load division details', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchDivisionData();
  }, [selectedDivision]);

  if (loading || !formData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sugarcane-green"></div>
        <p className="mt-4 text-gray-500 font-medium">Loading Division Content...</p>
      </div>
    );
  }

  // Handle direct field change
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle nested card text change
  const handleSubCardChange = (idx, field, value) => {
    setFormData(prev => {
      const updatedCards = [...(prev.introSubCards || [])];
      updatedCards[idx] = { ...updatedCards[idx], [field]: value };
      return { ...prev, introSubCards: updatedCards };
    });
  };

  // Handle items in services list
  const handleServiceChange = (itemIdx, field, value) => {
    setFormData(prev => {
      const updatedList = [...(prev.services || [])];
      updatedList[itemIdx] = { ...updatedList[itemIdx], [field]: value };
      return { ...prev, services: updatedList };
    });
  };

  // Add new service card
  const handleAddService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...(prev.services || []), { name: '', desc: '' }]
    }));
  };

  // Delete service card
  const handleDeleteService = (idx) => {
    setFormData(prev => ({
      ...prev,
      services: (prev.services || []).filter((_, i) => i !== idx)
    }));
  };

  // Handle gallery image updates
  const handleGalleryImageChange = (imgIdx, base64) => {
    setFormData(prev => {
      const updatedGallery = [...(prev.galleryImages || [])];
      updatedGallery[imgIdx] = base64;
      return { ...prev, galleryImages: updatedGallery };
    });
  };

  // Add new gallery image slot
  const handleAddGalleryImage = () => {
    setFormData(prev => ({
      ...prev,
      galleryImages: [...(prev.galleryImages || []), '']
    }));
  };

  // Delete gallery image slot
  const handleDeleteGalleryImage = (idx) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: (prev.galleryImages || []).filter((_, i) => i !== idx)
    }));
  };

  // Save to Firestore
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    Swal.fire({
      title: 'Saving Division Details...',
      text: 'Writing contents directly to Firestore',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const docRef = doc(db, 'divisions', selectedDivision);
      await setDoc(docRef, formData);
      
      Swal.fire({
        title: 'Success!',
        text: 'Division page content updated successfully.',
        icon: 'success',
        confirmButtonColor: '#0ea732'
      });
    } catch (error) {
      console.error("Error saving division data:", error);
      Swal.fire('Error', 'Failed to update content', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-premium p-8 max-w-6xl mx-auto">
      {/* Header and Division Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 mb-8 border-b border-gray-100 gap-4">
        <div>
          <h3 className="text-xl font-extrabold text-sugarcane-charcoal flex items-center gap-2">
            <Sparkles className="text-sugarcane-green" />
            Manage Division Contents
          </h3>
          <p className="text-gray-400 text-xs mt-1">Select the department/division below to customize its layout and contents.</p>
        </div>
        <div>
          <select
            value={selectedDivision}
            onChange={(e) => {
              setSelectedDivision(e.target.value);
              setActiveFormTab('hero-overview');
            }}
            className="w-full md:w-64 bg-gray-50 border border-gray-200 text-sugarcane-charcoal rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-sugarcane-green font-bold text-sm"
          >
            {divisionsList.map(div => (
              <option key={div.id} value={div.id}>{div.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 mb-8 space-x-6">
        <button
          type="button"
          onClick={() => setActiveFormTab('hero-overview')}
          className={`pb-4 text-sm font-bold flex items-center gap-2 transition-all ${
            activeFormTab === 'hero-overview'
              ? 'text-sugarcane-green border-b-2 border-sugarcane-green'
              : 'text-gray-400 hover:text-sugarcane-charcoal'
          }`}
        >
          <Layers size={18} />
          Hero & Overview
        </button>
        <button
          type="button"
          onClick={() => setActiveFormTab('lists')}
          className={`pb-4 text-sm font-bold flex items-center gap-2 transition-all ${
            activeFormTab === 'lists'
              ? 'text-sugarcane-green border-b-2 border-sugarcane-green'
              : 'text-gray-400 hover:text-sugarcane-charcoal'
          }`}
        >
          <List size={18} />
          Details & Services
        </button>
        <button
          type="button"
          onClick={() => setActiveFormTab('gallery')}
          className={`pb-4 text-sm font-bold flex items-center gap-2 transition-all ${
            activeFormTab === 'gallery'
              ? 'text-sugarcane-green border-b-2 border-sugarcane-green'
              : 'text-gray-400 hover:text-sugarcane-charcoal'
          }`}
        >
          <ImageIcon size={18} />
          Gallery Section
        </button>
      </div>

      {/* Language Switcher Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-emerald/5 p-4 rounded-2xl border border-emerald/10 mb-8 gap-3">
        <div className="flex items-center space-x-2 text-emerald">
          <Globe size={18} />
          <span className="text-xs font-bold uppercase tracking-wider text-sugarcane-charcoal">
            Form Language Mode: <span className="text-emerald">{formLang === 'en' ? 'English (🇬🇧)' : 'मराठी / Marathi (🇮🇳)'}</span>
          </span>
        </div>
        <div className="flex bg-white p-1 rounded-xl border border-gray-200 shadow-sm gap-1 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setFormLang('en')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
              formLang === 'en' ? 'bg-sugarcane-green text-white shadow-sm' : 'text-gray-500 hover:text-sugarcane-charcoal'
            }`}
          >
            English 🇬🇧
          </button>
          <button
            type="button"
            onClick={() => setFormLang('mr')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
              formLang === 'mr' ? 'bg-sugarcane-green text-white shadow-sm' : 'text-gray-500 hover:text-sugarcane-charcoal'
            }`}
          >
            मराठी 🇮🇳
          </button>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* Tab 1: Hero & Overview */}
        {activeFormTab === 'hero-overview' && (
          <div className="space-y-8">
            {/* HERO SECTION */}
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <h4 className="text-base font-extrabold text-sugarcane-charcoal mb-4 uppercase tracking-wider text-sugarcane-green flex items-center justify-between">
                <span>1. Hero Section ({formLang === 'mr' ? 'मराठी' : 'English'})</span>
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                    Hero Title {formLang === 'mr' ? '(मराठी मध्ये)' : '(English)'}
                  </label>
                  <input
                    type="text"
                    value={formLang === 'mr' ? (formData.heroTitleMr || '') : (formData.heroTitle || '')}
                    onChange={(e) => handleChange(formLang === 'mr' ? 'heroTitleMr' : 'heroTitle', e.target.value)}
                    placeholder={formLang === 'mr' ? 'उदा. प्रशासकीय विभाग' : 'e.g. Administration Department'}
                    className="w-full bg-white border border-gray-200 text-sugarcane-charcoal rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-sugarcane-green text-sm font-bold"
                  />
                </div>
              </div>
            </div>

            {/* INTRO/OVERVIEW SECTION */}
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <h4 className="text-base font-extrabold text-sugarcane-charcoal mb-4 uppercase tracking-wider text-sugarcane-green">
                2. Overview Section ({formLang === 'mr' ? 'मराठी' : 'English'})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                      Intro Label / Category {formLang === 'mr' ? '(मराठी)' : '(English)'}
                    </label>
                    <input
                      type="text"
                      value={formLang === 'mr' ? (formData.introLabelMr || '') : (formData.introLabel || '')}
                      onChange={(e) => handleChange(formLang === 'mr' ? 'introLabelMr' : 'introLabel', e.target.value)}
                      placeholder={formLang === 'mr' ? 'विभाग परिचय' : 'DEPARTMENT OVERVIEW'}
                      className="w-full bg-white border border-gray-200 text-sugarcane-charcoal rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-sugarcane-green text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                      Intro Description Text {formLang === 'mr' ? '(मराठी)' : '(English)'}
                    </label>
                    <textarea
                      rows={4}
                      value={formLang === 'mr' ? (formData.introDescMr || '') : (formData.introDesc || '')}
                      onChange={(e) => handleChange(formLang === 'mr' ? 'introDescMr' : 'introDesc', e.target.value)}
                      className="w-full bg-white border border-gray-200 text-sugarcane-charcoal rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-sugarcane-green text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Intro Section Main Image</label>
                  <ImageUpload
                    initialImage={formData.introImage}
                    onUploadSuccess={(base64) => handleChange('introImage', base64)}
                    folder={`${selectedDivision}-intro`}
                    maxSizeKb={800}
                  />
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Tab 2: Details & Services with Full CRUD */}
        {activeFormTab === 'lists' && (
          <div className="space-y-8">
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h4 className="text-base font-extrabold text-sugarcane-charcoal uppercase tracking-wider text-sugarcane-green">
                    3. Services & Systems ({formLang === 'mr' ? 'मराठी Content' : 'English Content'})
                  </h4>
                  <p className="text-gray-400 text-xs mt-1">Add, edit, or delete service info cards displayed on this department page.</p>
                </div>
                <button
                  type="button"
                  onClick={handleAddService}
                  className="flex items-center space-x-1.5 bg-sugarcane-green text-white hover:bg-forest px-4 py-2 rounded-xl font-bold text-xs transition-all shadow-sm"
                >
                  <Plus size={16} />
                  <span>Add New Service Card</span>
                </button>
              </div>

              {(!formData.services || formData.services.length === 0) ? (
                <div className="text-center py-8 text-gray-400 text-sm bg-white rounded-xl border border-dashed border-gray-200">
                  No service cards added yet. Click "Add New Service Card" above to add one.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {formData.services.map((srv, idx) => (
                    <div key={idx} className="p-4 bg-white rounded-xl border border-gray-150 space-y-3 relative group">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">
                          Card {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeleteService(idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Card"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 mb-1">
                          Title {formLang === 'mr' ? '(मराठी)' : '(English)'}
                        </label>
                        <input
                          type="text"
                          value={formLang === 'mr' ? (srv.nameMr || srv.titleMr || '') : (srv.name || srv.title || '')}
                          onChange={(e) => handleServiceChange(idx, formLang === 'mr' ? 'nameMr' : 'name', e.target.value)}
                          className="w-full border border-gray-250 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-sugarcane-green font-bold text-sugarcane-charcoal"
                          placeholder={formLang === 'mr' ? 'सेवा शीर्षक (मराठी)' : 'Card Title'}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 mb-1">
                          Description {formLang === 'mr' ? '(मराठी)' : '(English)'}
                        </label>
                        <textarea
                          rows={3}
                          value={formLang === 'mr' ? (srv.descMr || '') : (srv.desc || '')}
                          onChange={(e) => handleServiceChange(idx, formLang === 'mr' ? 'descMr' : 'desc', e.target.value)}
                          className="w-full border border-gray-250 rounded-lg py-2 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-sugarcane-green text-gray-600"
                          placeholder={formLang === 'mr' ? 'सेवा वर्णन (मराठी)' : 'Card Description'}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 3: Gallery Section with Full CRUD */}
        {activeFormTab === 'gallery' && (
          <div className="space-y-6">
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h4 className="text-base font-extrabold text-sugarcane-charcoal uppercase tracking-wider text-sugarcane-green">4. Gallery Images</h4>
                  <p className="text-gray-400 text-xs mt-1">Upload, update, or delete gallery images for this department page. (Under 800 KB each)</p>
                </div>
                <button
                  type="button"
                  onClick={handleAddGalleryImage}
                  className="flex items-center space-x-1.5 bg-sugarcane-green text-white hover:bg-forest px-4 py-2 rounded-xl font-bold text-xs transition-all shadow-sm"
                >
                  <Plus size={16} />
                  <span>Add New Image Slot</span>
                </button>
              </div>

              {(!formData.galleryImages || formData.galleryImages.length === 0) ? (
                <div className="text-center py-8 text-gray-400 text-sm bg-white rounded-xl border border-dashed border-gray-200">
                  No gallery images added yet. Click "Add New Image Slot" above to upload an image.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {formData.galleryImages.map((img, idx) => (
                    <div key={idx} className="p-4 bg-white rounded-xl border border-gray-150 space-y-2 relative">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-sugarcane-charcoal uppercase block">Slot {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => handleDeleteGalleryImage(idx)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Image"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <ImageUpload
                        initialImage={img}
                        onUploadSuccess={(base64) => handleGalleryImageChange(idx, base64)}
                        folder={`${selectedDivision}-gallery-${idx}`}
                        maxSizeKb={800}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 bg-sugarcane-green text-white hover:bg-forest px-8 py-4 rounded-xl font-bold tracking-wide text-sm transition-all duration-300 shadow-md shadow-sugarcane-green/10 disabled:opacity-55 cursor-pointer"
          >
            <Save size={18} />
            <span>{saving ? 'Saving...' : 'Save Division Changes'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminDivisions;
